import { httpsCallableWithTimeout, functions } from '@/lib/functions';
import type { LocalPicksRecommendationItem } from '@/services/aiPlanner';

export type GetCityPicksRequest = {
  city: string;
  latitude?: number;
  longitude?: number;
  radiusMeters?: number;
  ttlHours?: number;
  forceRefresh?: boolean;
};

type CityPickItem = {
  placeId: string;
  name: string;
  types?: string[];
  rating?: number;
  userRatingsTotal?: number;
  priceLevel?: number;
  address?: string;
  imageUrl?: string;
  location?: {
    latitude: number;
    longitude: number;
  };
  category?: 'landmarks' | 'dining' | 'trendy';
};

type GetCityPicksResponse = {
  cityKey: string;
  source: 'cache' | 'google';
  expiresAt: string;
  items: CityPickItem[];
};

const callGetCityPicks = httpsCallableWithTimeout<GetCityPicksRequest, GetCityPicksResponse>(
  functions,
  'getCityPicks',
  60000,
);

export async function requestCityPicks(payload: GetCityPicksRequest): Promise<GetCityPicksResponse> {
  try {
    const response = await callGetCityPicks(payload);
    return response.data;
  } catch (error: any) {
    throw error;
  }
}

// Temporary adapter: reuse existing "recommendation" UI types without refactoring.
export function mapCityPicksToRecommendations(items: CityPickItem[], city?: string): LocalPicksRecommendationItem[] {
  return (items || []).map((item) => ({
    id: `google-${item.placeId}`,
    type: 'poi',
    category: item.category,
    name: item.name,
    description: item.address,
    imageUrl: item.imageUrl,
    location: item.location
      ? { latitude: item.location.latitude, longitude: item.location.longitude, address: item.address }
      : undefined,
    rating: item.rating,
    metadata: {
      provider: 'google',
      city,
      placeId: item.placeId,
      types: item.types,
      priceLevel: item.priceLevel,
      userRatingsTotal: item.userRatingsTotal,
      category: item.category,
    },
  }));
}

/** Drop duplicate Places when merging picks from multiple trip segments (same placeId). */
export function dedupeLocalPicksByPlaceId(items: LocalPicksRecommendationItem[]): LocalPicksRecommendationItem[] {
  const seen = new Set<string>();
  const out: LocalPicksRecommendationItem[] = [];
  for (const item of items || []) {
    const placeId =
      typeof item.metadata?.placeId === 'string' && item.metadata.placeId.trim()
        ? item.metadata.placeId.trim()
        : '';
    const key = placeId || item.id;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}


